import { Component, HostListener, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { SidebarComponent } from '../shared/components/sidebar/sidebar.component';
import { TopbarComponent } from '../shared/components/topbar/topbar.component';

/**
 * The app shell, and the only thing that knows whether the nav is open.
 *
 * Above the drawer breakpoint the sidebar is a fixed column and `navOpen` is
 * simply never read — the CSS ignores it. Below it, the sidebar slides over the
 * content and this flag drives it. Keeping the state here rather than in the
 * sidebar means the backdrop, the body scroll lock and the topbar's button all
 * read one source instead of three that can disagree.
 */
@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, SidebarComponent, TopbarComponent],
  template: `
    <div class="app-shell" [class.nav-open]="navOpen()">
      <app-sidebar [open]="navOpen()" />

      <!-- Covers the content while the drawer is over it. A tap anywhere on it
           closes, which is the gesture people try before hunting for an X. -->
      <div class="nav-backdrop" *ngIf="navOpen()" (click)="closeNav()" aria-hidden="true"></div>

      <app-topbar [navOpen]="navOpen()" (toggleNav)="toggleNav()" />

      <main class="main-content">
        <div class="page-container">
          <router-outlet />
        </div>
      </main>
    </div>
  `,
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent {
  private router = inject(Router);

  navOpen = signal(false);

  constructor() {
    // Navigating closes the drawer. Without this, tapping a nav item leaves it
    // sitting open over the page it just opened.
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this.closeNav());
  }

  toggleNav() { this.setNav(!this.navOpen()); }
  closeNav()  { this.setNav(false); }

  /**
   * Escape closes it — the drawer is a modal surface while it covers content,
   * and every other overlay in this app answers to Escape.
   */
  @HostListener('document:keydown.escape')
  onEscape() { if (this.navOpen()) this.closeNav(); }

  /**
   * Locks the page behind the drawer.
   *
   * Without this the page scrolls under the open drawer on touch, which reads
   * as the drawer itself being broken.
   */
  private setNav(open: boolean) {
    this.navOpen.set(open);
    document.body.classList.toggle('nav-locked', open);
  }
}
